import './tiered-group-size-discount.scss';

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Slider } from 'antd';
import Text from 'antd/lib/typography/Text';

import ZLButton, { ButtonType, ButtonSize } from '../zl-button/zl-button';
import Currency from '../currency/currency';
import { IApplicationState } from '../../store';
import { calculateBookingEstimate } from './calculateBookingEstimate';
import { DISCOUNT_TIERS, CAMPAIGN_CONFIG } from './tieredGroupSizeDiscountConfig';

export interface ITieredGroupSizeDiscountProps {
	currentPlayers: number;
	maximumPlayers: number;
	locale: string;
	onAccept: (newPlayerCount: number) => boolean;
	variant: 'inline' | 'drawer';
	loading?: boolean;
}

const TieredGroupSizeDiscount: React.FunctionComponent<ITieredGroupSizeDiscountProps> = (props) => {
	const { t } = useTranslation();
	const [selectedCount, setSelectedCount] = useState<number>(props.currentPlayers);

	const selectedSession = useSelector((state: IApplicationState) => state.session.selectedSession);
	const bookingOptions = useSelector((state: IApplicationState) => state.sites.bookingOptions);
	const selectedPrivateUpgrade = useSelector((state: IApplicationState) => state.players.selectedPrivateUpgrade);

	const pricing = selectedSession?.package.packageTierPrices.find(
		(ptp) => ptp.priceTierType.priceTierTypeId === selectedSession.priceTierTypeId
	)?.price;
	const currencyCode = pricing?.currencyCode ?? '';

	useEffect(() => {
		setSelectedCount(props.currentPlayers);
	}, [props.currentPlayers]);

	const activeTier = [...DISCOUNT_TIERS].reverse().find((tier) => selectedCount >= tier.threshold);
	const discountRate = activeTier?.rate ?? 0;
	const estimate = pricing
		? calculateBookingEstimate({
			pricingTotal: pricing.total,
			pricingCost: pricing.cost,
			pricingTax: pricing.tax,
			playerCount: selectedCount,
			perPersonPricing: selectedSession?.package?.perPersonPricing ?? true,
			feeAmount: bookingOptions?.feeAmount,
			feePercent: bookingOptions?.feePercent,
			selectedPrivateUpgrade,
			privacyTypeId: selectedSession?.package?.privacyTypeId,
			privacyChargePercentage: selectedSession?.package?.privacyChargePercentage,
			maximumSlots: selectedSession?.maximumSlots,
			remainingSlots: selectedSession?.remainingSlots,
			discountRate,
			isPerSlot: CAMPAIGN_CONFIG.isPerSlot,
		})
		: undefined;
	const fullPrice = estimate?.total;
	const totalPrice = estimate?.discountedTotal;

	const hasChanged = selectedCount !== props.currentPlayers;

	const sliderMarks = useMemo(() => {
		const tierMap = new Map<number, string>(DISCOUNT_TIERS.map((tier) => [tier.threshold, tier.label]));
		const marks: Record<number, React.ReactNode> = {};
		for (let i = 1; i <= props.maximumPlayers; i++) {
			const tierLabel = tierMap.get(i);
			if (tierLabel) {
				marks[i] = (
					<span>
						<strong>{i}+</strong>
						<span className="upsell-threshold-label">{tierLabel}</span>
					</span>
				);
			} else {
				marks[i] = String(i);
			}
		}
		return marks;
	}, [props.maximumPlayers]);

	const handleAccept = () => {
		props.onAccept(selectedCount);
	};

	return (
		<div
			className={`tiered-group-size-discount tiered-group-size-discount-${props.variant}`}
			data-testid="tiered-group-size-discount-card"
		>
			{/* Header strip */}
			<div className="upsell-header" data-testid="tiered-group-size-discount-header">
				<Text strong className="upsell-header-badge">
					{t('tieredGroupSizeDiscount.headers.badge')}
				</Text>
				{DISCOUNT_TIERS.map((tier) => (
					<Text key={tier.threshold} className="upsell-header-offer">
						{t('tieredGroupSizeDiscount.headers.tier', { threshold: tier.threshold, discount: tier.label })}
					</Text>
				))}
			</div>

			{/* Body: slider + disclaimer */}
			<div className="upsell-body">
				<Text className="upsell-body-label">{t('tieredGroupSizeDiscount.labels.sliderLabel')}</Text>
				<div className="upsell-body-slider" data-testid="tiered-group-size-discount-slider">
					<Slider
						min={1}
						max={props.maximumPlayers}
						marks={sliderMarks}
						value={selectedCount}
						onChange={setSelectedCount}
						tooltip={{ formatter: (val) => t('tieredGroupSizeDiscount.labels.tooltipPlayers', { count: val }) }}
						disabled={props.loading}
					/>
				</div>
				<Text className="upsell-body-disclaimer">
					{t('tieredGroupSizeDiscount.labels.disclaimer')}
				</Text>
			</div>

			{/* Footer: total + update button */}
			<div className="upsell-footer">
				<div className="upsell-footer-info">
					<Text className="upsell-footer-label">{t('tieredGroupSizeDiscount.labels.footerLabel')}</Text>
					<div className="upsell-footer-count-group">
						<Text className="upsell-footer-count" data-testid="tiered-group-size-discount-player-count">
							{selectedCount}
						</Text>
						<Text className="upsell-footer-players">
							{selectedCount === 1
								? t('tieredGroupSizeDiscount.labels.player')
								: t('tieredGroupSizeDiscount.labels.player_plural')}
						</Text>
					</div>
					{totalPrice != null && currencyCode && (
						<span className="upsell-footer-price-group">
							<Text className="upsell-footer-price-label">
								{t('tieredGroupSizeDiscount.labels.totalLabel')}
							</Text>
							{discountRate > 0 && fullPrice != null && (
								<Currency
									value={fullPrice}
									currency={currencyCode}
									locale={props.locale}
									className="upsell-footer-price upsell-footer-price-original"
								/>
							)}
							<Currency
								value={totalPrice}
								currency={currencyCode}
								locale={props.locale}
								className="upsell-footer-price"
							/>
						</span>
					)}
				</div>
				{hasChanged && (
					<ZLButton
						type={ButtonType.Primary}
						size={ButtonSize.Large}
						onClick={handleAccept}
						buttonId="tiered-group-size-discount-update-btn"
						block={true}
						loading={props.loading}
						disabled={props.loading}
					>
						{t('tieredGroupSizeDiscount.labels.updateButton')}
					</ZLButton>
				)}
			</div>
		</div>
	);
};

export default TieredGroupSizeDiscount;
